import FormattedMessage from 'I18n/components/FormattedMessage';
import Env from 'enviro';
import { AbstractPageTemplate } from 'foundations-components/transitional/components/AbstractPageTemplate';
import { Big } from 'foundations-components/transitional/components/Big';
import { Button } from 'foundations-components/transitional/components/Button';
import { H5 } from 'foundations-components/transitional/components/H5';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { Logo } from 'foundations-components/transitional/components/Logo';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import _isNil from 'hs-lodash/isNil';
import _omitBy from 'hs-lodash/omitBy';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ACTIONS, SCREENS } from '../../../constants/Tracking';
import { REFRESH_COOKIE_TIMEOUT } from '../../../constants/timeouts';
import { AccountsPropsContext } from '../../../contexts/accounts/PropsContext';
import { defaultAccountsProps } from '../../../contexts/accounts/constants';
import { AccountsContext, AccountsContextProvider } from '../../../contexts/accounts/context';
import { AppContext } from '../../../contexts/app/context';
import useInterval from '../../../hooks/useInterval';
import { isAcceptanceTest } from '../../../utils/general';
import { navigateWithReload } from '../../../utils/helpers';
import { getBaseUrlWithCorrectHublet } from '../../../utils/urls';
import AccountList from '../AccountList';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function Accounts() {
  const {
    usageTracker,
    canViewParticipants,
    screenName,
    unauthorizedRoutesParam,
    routes,
    openUrl,
    connectNewAccountUrl,
    apiWrapper
  } = useContext(AccountsPropsContext);
  const {
    isLoading,
    accounts,
    accountsActions
  } = useContext(AccountsContext);
  const {
    appName
  } = useContext(AppContext);
  const history = useHistory();
  const navigateToPortal = useCallback(account => {
    const accountPortalId = account.portalId;
    const currentHublet = Env.getHublet();
    const routeToNavigate = canViewParticipants ? routes.contacts : routes.associations;
    if (currentHublet !== account.hublet) {
      navigateWithReload(`${getBaseUrlWithCorrectHublet(account.hublet)}/${accountPortalId}${routeToNavigate}${history.location.search}`);
    } else {
      history.push(`/${accountPortalId}${routeToNavigate}`);
    }
  }, [canViewParticipants, history, routes.associations, routes.contacts]);
  useInterval(apiWrapper.fetchUserInfo, REFRESH_COOKIE_TIMEOUT, {
    forceRefresh: true,
    cancelOnUnmount: true
  });
  useEffect(() => {
    usageTracker.trackPageView(SCREENS.accounts);
  }, [usageTracker]);
  useEffect(() => {
    accountsActions.fetchAccounts();
  }, [accountsActions]);

  // if user don`t have an account -> redirect to reconnect page
  useEffect(() => {
    if (!isLoading && !accounts.length) {
      history.push(routes.reconnect);
    }
  }, [accounts, isLoading, history, routes.reconnect]);

  // aouto choose user account if user have only one available account
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const autochange = params.get(unauthorizedRoutesParam);
    if (accounts.length === 1 && !autochange && !isAcceptanceTest) {
      navigateToPortal(accounts[0]);
    }
  }, [navigateToPortal, accounts, history, unauthorizedRoutesParam]);
  const onClickRow = account => {
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS.accounts
    }, ACTIONS.accounts.connect));
    navigateToPortal(account);
  };
  const connectNewAccount = () => {
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS.accounts
    }, ACTIONS.accounts.connectNew));
    openUrl(connectNewAccountUrl);
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: isLoading ? /*#__PURE__*/_jsx(LoadingOverlay, {}) : /*#__PURE__*/_jsx("div", {
      "data-test-id": `${appName}-accounts-page`,
      children: /*#__PURE__*/_jsxs(AbstractPageTemplate, {
        narrowLayout: true,
        headerComponent: /*#__PURE__*/_jsx(View, {
          marginBlock: "500",
          marginInline: "auto",
          as: "div",
          children: /*#__PURE__*/_jsx(Logo, {
            name: "hubspot-wordmark",
            height: "40px",
            width: "100%"
          })
        }),
        horizontalDivider: false,
        children: [/*#__PURE__*/_jsx(H5, {
          marginBlockEnd: "100",
          children: /*#__PURE__*/_jsx(TruncateString, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "hubspotAccount.accountsList.welcome",
              options: {
                screenName
              }
            })
          })
        }), accounts.length > 0 && /*#__PURE__*/_jsx(Big, {
          use: "help",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "hubspotAccount.accountsList.choose"
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlockStart: "500",
          as: "div",
          children: /*#__PURE__*/_jsx(AccountList, {
            onClickRow: onClickRow
          })
        }), /*#__PURE__*/_jsxs(TFlex, {
          direction: "column",
          marginBlockStart: "1300",
          children: [accounts.length > 0 && /*#__PURE__*/_jsx(Big, {
            use: "help",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "hubspotAccount.accountsList.switch"
            })
          }), /*#__PURE__*/_jsx(TFlex, {
            alignSelf: "center",
            marginBlockStart: "200",
            children: /*#__PURE__*/_jsx(Button, {
              use: "primary",
              onClick: connectNewAccount,
              "data-test-id": `${appName}-accounts-connect-button`,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "hubspotAccount.accountsList.connect.button"
              })
            })
          })]
        })]
      })
    })
  });
}
const WrappedAccounts = props => {
  const definedProps = _omitBy(props, _isNil);
  return /*#__PURE__*/_jsx(AccountsPropsContext.Provider, {
    value: Object.assign({}, defaultAccountsProps, definedProps),
    children: /*#__PURE__*/_jsx(AccountsContextProvider, {
      children: /*#__PURE__*/_jsx(Accounts, {})
    })
  });
};
export default WrappedAccounts;